import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import {
  GetScPseudocodeBIZcoreTD,
  GetCh99SeedBatchesBIZcoreTD,
  SeedCh99BatchBIZcoreTD,
  GetScCh99RulesBIZcoreTD,
  SeedCh99RulesBIZcoreTD,
  SeedCh99Subchapter3BIZcoreTD,
  WipeCh99RulesBIZcoreTD,
  GetCh99RuleCoverageBIZcoreTD
} from '../../../../../redux/actions/business/core/scCh99RuleEngineActions';

import * as api from '../../../../../api/index';
import { toast } from 'react-toastify';

const STATUS_CHIP_STYLES = {
  PENDING: { backgroundColor: '#854d0e', color: '#fbbf24' },
  DRAFT: { backgroundColor: '#854d0e', color: '#fbbf24' },
  APPROVED: { backgroundColor: '#166534', color: '#4ade80' },
  REJECTED: { backgroundColor: '#7f1d1d', color: '#f87171' },
  SUPERSEDED: { backgroundColor: '#334155', color: '#94a3b8' },
  CONVERSION_GAP: { backgroundColor: '#92400e', color: '#fb923c' },
};

// Monospace block for pseudocode and JSON — the whole point of these panels is
// that the author can read exactly what was compiled.
const CodeBlock = ({ title, children, accent = '#3b82f6' }) => (
  <Box sx={{ mb: '14px' }}>
    <Typography variant="caption" fontWeight={700}
      sx={{ color: accent, display: 'block', mb: '4px', letterSpacing: '0.3px' }}>
      {title}
    </Typography>
    <Box component="pre" sx={{
      m: 0,
      p: '12px 14px',
      backgroundColor: '#020617',
      border: '1px solid #1e293b',
      borderRadius: '6px',
      color: '#cbd5e1',
      fontSize: '12px',
      lineHeight: 1.6,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: '420px',
      overflow: 'auto',
    }}>
      {children}
    </Box>
  </Box>
);

const JsonBlock = ({ title, value, accent }) => {
  const isEmpty = value === null || value === undefined ||
    (typeof value === 'object' && Object.keys(value).length === 0);
  return (
    <CodeBlock title={title} accent={accent}>
      {isEmpty ? 'null' : JSON.stringify(value, null, 2)}
    </CodeBlock>
  );
};

// Rule engine sync is local-only — rules are authored locally, then pushed up.
const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const REMOTE_TARGETS = [
  { label: 'Alpha', url: process.env.REACT_APP_ALPHA_CORE_RULES_URL || 'https://alpha-api.lusid.ai/us/core/web/v1/trd/ch99-rules-import' },
  { label: 'Production', url: process.env.REACT_APP_PROD_CORE_RULES_URL || 'https://api.lusidpay.com/us/core/web/v1/trd/ch99-rules-import' },
];

const Ch99RuleEnginePage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);

  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [seeding, setSeeding] = useState(false);

  const [coverage, setCoverage] = useState(null);
  const [batches, setBatches] = useState([]);
  const [seedingBatch, setSeedingBatch] = useState(null);
  const [seedingSc3, setSeedingSc3] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [wiping, setWiping] = useState(false);
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [pushing, setPushing] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  // Which rows are expanded, keyed by row id, per tab.
  const [expandedPseudocode, setExpandedPseudocode] = useState({});
  const [expandedRules, setExpandedRules] = useState({});

  const toggleRow = (setter) => (id) =>
    setter(prev => ({ ...prev, [id]: !prev[id] }));

  // Pseudocode tab state
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewSort, setReviewSort] = useState(null);
  const [reviewSortDirection, setReviewSortDirection] = useState("asc");
  const [reviewList, setReviewList] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [
    reviewPage,
    reviewRowsPerPage,
    handleChangeReviewPage,
    handleChangeReviewRowsPerPage
  ] = useTablePaginationInit();

  // Compiled Rules tab state
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesSort, setRulesSort] = useState(null);
  const [rulesSortDirection, setRulesSortDirection] = useState("asc");
  const [rulesList, setRulesList] = useState([]);
  const [rulesTotal, setRulesTotal] = useState(0);
  const [
    rulesPage,
    rulesRowsPerPage,
    handleChangeRulesPage,
    handleChangeRulesRowsPerPage
  ] = useTablePaginationInit();

  const reviewHeadings = [
    { label: "", sort: null },
    { label: "Framework", sort: "framework_label" },
    { label: "Source Node", sort: "node_label_path" },
    { label: "Revision", sort: null },
    { label: "Grammar", sort: "grammar_version" },
    { label: "Status", sort: "status" },
    { label: "Created", sort: "created_at" },
  ];

  const rulesHeadings = [
    { label: "", sort: null },
    { label: "Ch99 Code", sort: "ch99_code" },
    { label: "Provision Type", sort: "provision_type" },
    { label: "Rule Type", sort: "rule_type" },
    { label: "Rate", sort: null },
    { label: "Scope", sort: null },
    { label: "Conditions", sort: null },
    { label: "Created", sort: "created_at" },
  ];

  const handleReviewSort = (value) => {
    setReviewSortDirection(reviewSortDirection === "asc" ? "desc" : "asc");
    setReviewSort(value);
  };

  const handleRulesSort = (value) => {
    setRulesSortDirection(rulesSortDirection === "asc" ? "desc" : "asc");
    setRulesSort(value);
  };

  // Fetch Pseudocode (parse reviews)
  useEffect(() => {
    if (activeTab === 0) handleGetReviews();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewPage, reviewRowsPerPage, search, reviewSort, reviewSortDirection, activeTab]);

  // Fetch Compiled Rules
  useEffect(() => {
    if (activeTab === 1) handleGetRules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesPage, rulesRowsPerPage, search, rulesSort, rulesSortDirection, activeTab]);

  useEffect(() => {
    try {
      setReviewList(state.scPseudocodeList?.records || []);
      setReviewTotal(state.scPseudocodeList?.total || 0);
    } catch (err) {}
  }, [state.scPseudocodeList]);

  useEffect(() => {
    try {
      setRulesList(state.scCh99RulesList?.records || []);
      setRulesTotal(state.scCh99RulesList?.total || 0);
    } catch (err) {}
  }, [state.scCh99RulesList]);

  async function handleGetReviews() {
    let query = "";
    if (search) query += `&search=${search}`;
    if (reviewSort) query += `&ordering=${reviewSortDirection === "asc" ? '' : '-'}${reviewSort}`;
    dispatch(GetScPseudocodeBIZcoreTD(reviewPage + 1, reviewRowsPerPage, query, true, setReviewLoading));
  }

  async function handleGetRules() {
    let query = "";
    if (search) query += `&search=${search}`;
    if (rulesSort) query += `&ordering=${rulesSortDirection === "asc" ? '' : '-'}${rulesSort}`;
    dispatch(GetScCh99RulesBIZcoreTD(rulesPage + 1, rulesRowsPerPage, query, true, setRulesLoading));
  }

  const handleSeed = () => {
    dispatch(SeedCh99RulesBIZcoreTD(setSeeding)).then(() => handleGetCoverage());
  };

  useEffect(() => {
    handleGetCoverage();
    dispatch(GetCh99SeedBatchesBIZcoreTD(setBatches));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each batch is a group of Subchapter III notes. The backend declares them, so
  // a new batch appears here without a frontend change.
  const handleSeedBatch = (batchKey) => {
    dispatch(SeedCh99BatchBIZcoreTD(batchKey, setSeedingBatch, setSeedResult)).then(() => {
      handleGetCoverage();
      dispatch(GetCh99SeedBatchesBIZcoreTD(setBatches));
      if (activeTab === 0) handleGetReviews(); else handleGetRules();
    });
  };

  function handleGetCoverage() {
    dispatch(GetCh99RuleCoverageBIZcoreTD(null, null, setCoverage));
  }

  const handleSeedSubchapter3 = () => {
    dispatch(SeedCh99Subchapter3BIZcoreTD(setSeedingSc3, setSeedResult)).then(() => {
      handleGetCoverage();
      if (activeTab === 0) handleGetReviews(); else handleGetRules();
    });
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeCh99RulesBIZcoreTD(setWiping)).then(() => {
      setPushResult(null);
      setSeedResult(null);
      handleGetCoverage();
      if (activeTab === 0) handleGetReviews(); else handleGetRules();
    });
  };

  // Export the six rule engine tables locally, then POST them at the remote.
  // The remote wipes its own rule tables and rebuilds them from this payload.
  const handlePushToRemote = async (target) => {
    if (!target.url) {
      toast.error(`No URL configured for ${target.label}`);
      return;
    }
    setPushing(target.label);
    setPushResult(null);
    try {
      toast.info('Exporting local rule engine tables...');
      const { data: exportResp } = await api.exportCh99RulesBIZcoreTD();
      const exportData = exportResp.data;

      if (!exportData || !exportData.stats || exportData.stats.rules === 0) {
        toast.error('No compiled rules to push. Compile rules first.');
        setPushing(null);
        return;
      }

      toast.info(`Pushing ${exportData.stats.rules} rules + ${exportData.stats.mappings} mappings to ${target.label}...`);
      const { data: importResp, status } = await api.pushCh99RulesToRemote(target.url, exportData);

      if (status === 201) {
        const pushed = importResp.data || {};
        setPushResult({ target: target.label, ...pushed });
        if (pushed.skipped_count > 0) {
          toast.warn(`Pushed to ${target.label} with ${pushed.skipped_count} skipped row(s) — push the SC substrate there first`);
        } else {
          toast.success(`Pushed to ${target.label} successfully`);
        }
      } else {
        toast.error(`Push to ${target.label} failed`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Push to ${target.label} failed: ${error.response?.data?.error || error.message}`);
    }
    setPushing(null);
  };

  // Compact summaries so the collapsed row still says what the rule does.
  const mono = (text, color = '#e2e8f0') => (
    <Typography variant="caption" sx={{ fontFamily: 'monospace', color }}>{text}</Typography>
  );

  const describeRate = (rate) => {
    if (!rate?.type) return <NA />;
    if (rate.type === 'AD_VALOREM') return mono(`${rate.rate_pct}%`, '#4ade80');
    if (rate.type === 'TOP_UP_TO') return mono(`→ ${rate.target_pct}%`, '#4ade80');
    if (rate.type === 'SPECIFIC') return mono(`$${rate.amount_usd}/${rate.unit}`, '#4ade80');
    if (rate.type === 'CONVERSION_GAP') return mono('rate pending', '#fb923c');
    return mono(rate.type, '#94a3b8');
  };

  const describeScope = (scope) => {
    if (!scope?.type) return <NA />;
    if (scope.type === 'ALL') return mono('all countries', '#94a3b8');
    const countries = scope.countries || [];
    const label = countries.length > 3
      ? `${countries.slice(0, 3).join(', ')} +${countries.length - 3}`
      : countries.join(', ');
    return mono(`${scope.type === 'COUNTRY_EXCLUDE' ? 'except ' : ''}${label}`, '#fbbf24');
  };

  const describeConditions = (conditions) => {
    if (!conditions) return mono('none', '#475569');
    if (conditions.type === 'COMPOUND') {
      const count = (conditions.clauses || []).length;
      return mono(`${conditions.operator || 'AND'} · ${count} clause${count === 1 ? '' : 's'}`, '#f472b6');
    }
    return mono(conditions.type, '#f472b6');
  };

  const getStatusChipSx = (status) => {
    const styles = STATUS_CHIP_STYLES[status] || { backgroundColor: '#1e293b', color: '#94a3b8' };
    return { ...styles, fontSize: '11px', height: '22px' };
  };

  const loading = activeTab === 0 ? reviewLoading : rulesLoading;
  const currentList = activeTab === 0 ? reviewList : rulesList;

  return (
    <Box>
      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe all Ch99 rule engine tables"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <TopBar
        title={"Ch99 Rule Engine"}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        button4Text={seedingSc3 ? "Seeding..." : "Seed Subchapter III"}
        button4OnClick={handleSeedSubchapter3}
        button4Disabled={seedingSc3 || wiping || !!pushing}
        button3Text={seeding ? "Seeding..." : "Seed Subchapter I Data"}
        button3OnClick={handleSeed}
      />

      {/* ── Seed batches — one button per group of notes ──────────────────── */}
      {batches.length > 0 && (
        <Box sx={{ border: '1px solid #334155', borderRadius: '8px', mb: '16px', overflow: 'hidden' }}>
          <Box sx={{ px: '16px', py: '10px', background: '#0f172a', borderBottom: '1px solid #334155' }}>
            <Typography variant="subtitle2" color="#94a3b8">Subchapter III — Seed Batches</Typography>
          </Box>
          <Box sx={{ px: '16px', py: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography variant="body2" color="#64748b">
              Each batch writes its pseudocode and compiles its rules across every revision that
              carries Ch.99 content. Re-running a batch replaces only its own rows.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {batches.map((batch) => (
                <Box key={batch.key} sx={{
                  border: '1px solid #334155', borderRadius: '8px', px: '14px', py: '12px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb="6px">
                    <Typography variant="body2" color="#e2e8f0" fontWeight={600}>{batch.label}</Typography>
                    <Chip
                      label={batch.frameworks_seeded > 0
                        ? `${batch.frameworks_seeded} in ${batch.revisions_seeded} rev`
                        : 'not seeded'}
                      size="small"
                      sx={batch.frameworks_seeded > 0
                        ? { backgroundColor: '#166534', color: '#4ade80', fontSize: '11px', height: '20px' }
                        : { backgroundColor: '#1e293b', color: '#64748b', fontSize: '11px', height: '20px' }}
                    />
                  </Box>
                  {/* Which notes this batch covers — with six batches the label alone
                      no longer says what is inside one. */}
                  <Box display="flex" gap="4px" flexWrap="wrap" mb="8px">
                    {(batch.notes || []).map((note, i) => (
                      <Box key={i} sx={{
                        fontSize: '10px', fontWeight: 700, fontFamily: 'monospace',
                        color: '#93c5fd', backgroundColor: '#1e3a5f',
                        borderRadius: '4px', px: '5px', py: '1px',
                      }}>
                        {note}
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: '10px' }}>
                    {batch.description}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={seedingBatch === batch.key
                      ? <CircularProgress size={14} />
                      : <PlaylistAddCheckOutlinedIcon />}
                    disabled={!!seedingBatch || wiping || !!pushing}
                    onClick={() => handleSeedBatch(batch.key)}
                  >
                    {seedingBatch === batch.key ? 'Seeding...' : `Seed ${batch.label}`}
                  </Button>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              startIcon={seedingBatch === 'ALL' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlaylistAddCheckOutlinedIcon />}
              disabled={!!seedingBatch || wiping || !!pushing}
              onClick={() => handleSeedBatch('ALL')}
              sx={{ alignSelf: 'flex-start' }}
            >
              {seedingBatch === 'ALL' ? 'Seeding all batches...' : 'Seed All Batches'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Compiled coverage — what the engine actually knows ────────────── */}
      {coverage && (
        <Box sx={{ border: '1px solid #334155', borderRadius: '8px', mb: '16px', overflow: 'hidden' }}>
          <Box sx={{ px: '16px', py: '10px', background: '#0f172a', borderBottom: '1px solid #334155' }}>
            <Typography variant="subtitle2" color="#94a3b8">Compiled Coverage</Typography>
          </Box>
          <Box sx={{ px: '16px', py: '12px', display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Revision', value: coverage.revision?.revision_name || '—' },
              { label: 'Frameworks', value: coverage.frameworks },
              { label: 'Rules', value: coverage.rules },
              { label: 'HTS Mappings', value: coverage.mappings },
            ].map((item, i) => (
              <Box key={i}>
                <Typography variant="caption" color="#64748b">{item.label}</Typography>
                <Typography variant="body2" color="#e2e8f0" fontWeight={600}>{item.value}</Typography>
              </Box>
            ))}
            <Box>
              <Typography variant="caption" color="#64748b">Covered Notes</Typography>
              <Box display={"flex"} gap={"6px"} flexWrap={"wrap"} mt={"2px"}>
                {coverage.covered_notes?.length
                  ? coverage.covered_notes.map((n, i) => (
                    <Chip key={i} label={`${n.subchapter} · ${n.note}`} size="small"
                      sx={{ backgroundColor: '#1e3a5f', color: '#93c5fd', fontSize: '11px', height: '20px' }} />
                  ))
                  : <Typography variant="body2" color="#64748b">None compiled yet</Typography>}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Last Subchapter III seed run ──────────────────────────────────── */}
      {seedResult && (
        <Box sx={{ border: '1px solid #334155', borderRadius: '8px', mb: '16px', overflow: 'hidden' }}>
          <Box sx={{ px: '16px', py: '10px', background: '#166534' }}>
            <Typography variant="subtitle2" color="#fff">
              Subchapter III seeded from revision {seedResult.revision}
            </Typography>
          </Box>
          <Box sx={{ px: '16px', py: '12px' }}>
            <Typography variant="body2" color="#94a3b8">
              {seedResult.frameworks} frameworks, {seedResult.members} verbatim members,{' '}
              {seedResult.parse_reviews} pseudocode records, {seedResult.rules} compiled rules,{' '}
              {seedResult.interactions} interactions, {seedResult.mappings} HTS mappings
            </Typography>
            {seedResult.conversion_gap_count > 0 && (
              <Box mt={"10px"}>
                <Typography variant="caption" color="#fbbf24" fontWeight={600} sx={{ display: 'block', mb: '4px' }}>
                  {seedResult.conversion_gap_count} rule(s) stored with a CONVERSION_GAP — the note cites a
                  rate that lives in a tariff-table row this revision's extract does not carry
                </Typography>
                {(seedResult.conversion_gaps || []).map((gap, i) => (
                  <Typography key={i} variant="caption" color="#fcd34d" sx={{ display: 'block' }}>
                    {gap.ch99_code} — {gap.field}
                  </Typography>
                ))}
              </Box>
            )}
            {seedResult.skipped_count > 0 && (
              <Box mt={"10px"}>
                <Typography variant="caption" color="#f87171" fontWeight={600} sx={{ display: 'block', mb: '4px' }}>
                  {seedResult.skipped_count} item(s) skipped
                </Typography>
                {(seedResult.skipped || []).map((s, i) => (
                  <Typography key={i} variant="caption" color="#fca5a5" sx={{ display: 'block' }}>
                    {s.framework} — {s.reason}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Sync: wipe + push the six rule tables as one unit ─────────────── */}
      {IS_LOCAL && (
        <Box sx={{ border: '1px solid #334155', borderRadius: '8px', mb: '16px', overflow: 'hidden' }}>
          <Box sx={{ px: '16px', py: '10px', background: '#0f172a', borderBottom: '1px solid #334155' }}>
            <Typography variant="subtitle2" color="#94a3b8">Rule Engine Sync</Typography>
          </Box>
          <Box sx={{ px: '16px', py: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography variant="body2" color="#64748b">
              Pushes all six Ch99 rule engine tables as one unit. The target environment wipes its own
              rule tables and rebuilds them from this payload — push the SC substrate there first, since
              rules resolve against its nodes and revisions.
            </Typography>
            <Box display={"flex"} gap={"12px"} flexWrap={"wrap"}>
              {REMOTE_TARGETS.filter(t => t.url).map((target) => (
                <Button
                  key={target.label}
                  variant="outlined"
                  startIcon={pushing === target.label ? <CircularProgress size={16} /> : <CloudSyncOutlinedIcon />}
                  disabled={!!pushing || wiping}
                  onClick={() => handlePushToRemote(target)}
                >
                  {pushing === target.label ? `Pushing to ${target.label}...` : `Push to ${target.label}`}
                </Button>
              ))}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepOutlinedIcon />}
                disabled={!!pushing || wiping}
                onClick={() => setWipeConfirmOpen(true)}
              >
                {wiping ? 'Wiping...' : 'Wipe Rule Tables'}
              </Button>
            </Box>

            {pushResult && (
              <Box sx={{ border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{ px: '16px', py: '10px', background: pushResult.skipped_count > 0 ? '#92400e' : '#1e40af' }}>
                  <Typography variant="subtitle2" color="#fff">Pushed to {pushResult.target}</Typography>
                </Box>
                <Box sx={{ px: '16px', py: '12px' }}>
                  <Typography variant="body2" color="#94a3b8">
                    {pushResult.frameworks} frameworks, {pushResult.members} members, {pushResult.parse_reviews} pseudocode records,{' '}
                    {pushResult.rules} rules, {pushResult.interactions} interactions, {pushResult.mappings} mappings
                  </Typography>
                  {pushResult.skipped_count > 0 && (
                    <Box mt={"8px"}>
                      <Typography variant="caption" color="#fbbf24" fontWeight={600} sx={{ display: 'block', mb: '4px' }}>
                        {pushResult.skipped_count} row(s) skipped — the target is missing the SC nodes or revisions they reference
                      </Typography>
                      {(pushResult.skipped || []).slice(0, 8).map((s, i) => (
                        <Typography key={i} variant="caption" color="#fcd34d" sx={{ display: 'block' }}>
                          {s.table}: {s.label} — {s.reason}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: '#334155', mb: '0px' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none' },
            '& .Mui-selected': { color: '#e2e8f0' },
            '& .MuiTabs-indicator': { backgroundColor: '#3b82f6' },
          }}
        >
          <Tab label="Pseudocode" />
          <Tab label="Compiled Rules" />
        </Tabs>
      </Box>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          currentList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>
              {activeTab === 0 ? "No pseudocode records found" : "No compiled rules found"}
            </Typography>
          </Box>
          :
          activeTab === 0
          ?
          /* Pseudocode Tab */
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  reviewHeadings.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={reviewSort === heading.sort}
                        direction={reviewSortDirection}
                        onClick={() => handleReviewSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                reviewList.map((row, index) => (
                  <React.Fragment key={row.id || index}>
                    <TableRow
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedPseudocode[row.id] ? 'unset' : undefined } }}
                      onClick={() => toggleRow(setExpandedPseudocode)(row.id)}>
                      <TableCell sx={{ width: '40px' }}>
                        {expandedPseudocode[row.id]
                          ? <KeyboardArrowUpIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                          : <KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: 18 }} />}
                      </TableCell>
                      <TableCell>{row.framework_label || <NA />}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#93c5fd' }}>
                          {row.node_label_path || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8">
                          {row.revision_name || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#64748b">
                          {row.grammar_version || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.status || 'UNKNOWN'} size="small" sx={getStatusChipSx(row.status)} />
                      </TableCell>
                      <TableCell>
                        <TableTimeStamps createdAt={row.created_at} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedPseudocode[row.id] ? '1px solid #334155' : 'none' }}>
                        <Collapse in={!!expandedPseudocode[row.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: '18px' }}>
                            <Box display="flex" gap="28px" flexWrap="wrap" mb="16px">
                              {[
                                { label: 'Ch.99 codes', value: (row.meta_data?.ch99_codes || []).join(', ') || '—' },
                                { label: 'Source content linked', value: row.ref_content_id ? 'Yes' : 'No content row' },
                                { label: 'Source text hash', value: row.source_text_hash ? row.source_text_hash.slice(0, 16) + '…' : '—' },
                                { label: 'Authored by', value: row.authored_by || '—' },
                              ].map((item, i) => (
                                <Box key={i}>
                                  <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>{item.label}</Typography>
                                  <Typography variant="body2" color="#e2e8f0">{item.value}</Typography>
                                </Box>
                              ))}
                            </Box>

                            <CodeBlock title="PSEUDOCODE">
                              {row.pseudocode_text || '(empty)'}
                            </CodeBlock>

                            {row.raw_legal_text && (
                              <CodeBlock title="SOURCE LEGAL TEXT (verbatim)" accent="#94a3b8">
                                {row.raw_legal_text}
                              </CodeBlock>
                            )}

                            <JsonBlock title="META DATA" value={row.meta_data} accent="#64748b" />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={7}
                  count={reviewTotal}
                  rowsPerPage={reviewRowsPerPage}
                  page={reviewPage}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangeReviewPage}
                  onRowsPerPageChange={handleChangeReviewRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
          :
          /* Compiled Rules Tab */
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  rulesHeadings.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={rulesSort === heading.sort}
                        direction={rulesSortDirection}
                        onClick={() => handleRulesSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                rulesList.map((row, index) => (
                  <React.Fragment key={row.id || index}>
                    <TableRow
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRules[row.id] ? 'unset' : undefined } }}
                      onClick={() => toggleRow(setExpandedRules)(row.id)}>
                      <TableCell sx={{ width: '40px' }}>
                        {expandedRules[row.id]
                          ? <KeyboardArrowUpIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                          : <KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: 18 }} />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e2e8f0' }}>
                          {row.ch99_code || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8">{row.provision_type || <NA />}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8">{row.rule_type || <NA />}</Typography>
                      </TableCell>
                      <TableCell>{describeRate(row.rate_json)}</TableCell>
                      <TableCell>{describeScope(row.scope_json)}</TableCell>
                      <TableCell>{describeConditions(row.conditions_json)}</TableCell>
                      <TableCell>
                        <TableTimeStamps createdAt={row.created_at} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRules[row.id] ? '1px solid #334155' : 'none' }}>
                        <Collapse in={!!expandedRules[row.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: '18px' }}>
                            <Box display="flex" gap="28px" flexWrap="wrap" mb="16px">
                              {[
                                { label: 'Citation', value: row.source_citation_text || '—' },
                                { label: 'Effective from', value: row.effective_from || 'no start bound' },
                                { label: 'Effective to', value: row.effective_to || 'no end bound' },
                                { label: 'Compiler', value: row.pseudocode_to_jsonb_method || '—' },
                              ].map((item, i) => (
                                <Box key={i}>
                                  <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>{item.label}</Typography>
                                  <Typography variant="body2" color="#e2e8f0">{item.value}</Typography>
                                </Box>
                              ))}
                            </Box>

                            {row.rule_description && (
                              <Typography variant="body2" color="#94a3b8" sx={{ mb: '6px' }}>
                                {row.rule_description}
                              </Typography>
                            )}
                            {row.requirement_text && (
                              <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: '16px' }}>
                                {row.requirement_text}
                              </Typography>
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
                              <JsonBlock title="RATE JSON" value={row.rate_json} accent="#4ade80" />
                              <JsonBlock title="SCOPE JSON" value={row.scope_json} accent="#fbbf24" />
                            </Box>
                            <JsonBlock title="CONDITIONS JSON" value={row.conditions_json} accent="#f472b6" />
                            <JsonBlock title="META DATA" value={row.meta_data} accent="#64748b" />

                            {row.pseudocode_representation && (
                              <CodeBlock title="PSEUDOCODE THIS RULE WAS COMPILED FROM" accent="#93c5fd">
                                {row.pseudocode_representation}
                              </CodeBlock>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={8}
                  count={rulesTotal}
                  rowsPerPage={rulesRowsPerPage}
                  page={rulesPage}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangeRulesPage}
                  onRowsPerPageChange={handleChangeRulesRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  );
};

export default Ch99RuleEnginePage;
